import { IconButton, Switch, SwitchProps, Tooltip } from '@mui/material'
import { AlertPhone, RuuviTag, UserAlertRule, UserAlertRuleInput } from '../../types'
import { usePolicy } from '../../hooks'
import { Warning } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import { isAlertPhoneVerified } from '../../utils'
import { updateAlertPhoneAlertRules } from '../../services/alertPhoneService'

export interface AlertPhoneAlertRuleSmsNotificationsSwitchProps extends Omit<
  SwitchProps,
  'checked' | 'onChange'
> {
  alertPhone: AlertPhone
  ruuviTag?: RuuviTag
  alertRule: UserAlertRule
  alertRules: UserAlertRule[]
  beforeSubmit?: (input: UserAlertRuleInput[]) => void
  afterSubmit?: (alertPhoneAlertRules: UserAlertRule[]) => void
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
}

export default function AlertPhoneAlertRuleSmsNotificationsSwitch({
  alertPhone,
  ruuviTag,
  alertRule,
  alertRules,
  disabled,
  size = 'small',
  beforeSubmit,
  afterSubmit,
  onChange,
  ...props
}: AlertPhoneAlertRuleSmsNotificationsSwitchProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { canEditAlertPhone } = usePolicy()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * Handle the "change" event.
   */
  async function handleChange(event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    try {
      setLoading(true)

      const newAlertPhoneAlertRules = alertRules.map((rule) =>
        rule.id === alertRule.id
          ? ({
              ...rule,
              ruuviTagId: ruuviTag?.id,
              emailNotificationEnabled: false,
              smsNotificationEnabled: checked,
              appNotificationEnabled: false
            } as UserAlertRule)
          : rule
      )

      beforeSubmit?.(newAlertPhoneAlertRules)

      const updatedAlertPhoneAlertRules = await updateAlertPhoneAlertRules(
        alertPhone.id,
        newAlertPhoneAlertRules
      )

      afterSubmit?.(updatedAlertPhoneAlertRules)

      dispatch(
        createSuccessNotification(
          checked ? t('enableSmsNotificationSuccess') : t('disableSmsNotificationSuccess')
        )
      )

      onChange?.(event, checked)
    } catch (_error) {
      dispatch(
        createErrorNotification(
          checked ? t('enableSmsNotificationError') : t('disableSmsNotificationError')
        )
      )
    } finally {
      setLoading(false)
    }
  }

  if (!canEditAlertPhone) {
    return null
  }

  return isAlertPhoneVerified(alertPhone) ? (
    <Switch
      value={alertRule.smsNotificationEnabled}
      checked={alertRule.smsNotificationEnabled}
      disabled={disabled || loading}
      size={size}
      {...props}
      onChange={handleChange}
    />
  ) : (
    <Tooltip title={t('phoneIsNotVerified')}>
      <IconButton size={size} color="warning">
        <Warning />
      </IconButton>
    </Tooltip>
  )
}
